import { ToolUIPart } from "ai";

import { ConnectedOrganizationDashboards } from "../OrganizationDashboards/ConnectedOrganizationDashboards";
import { ConnectedPaymentCards } from "../PaymentCards/ConnectedPaymentCards";
import { parsePaymentCards } from "../PaymentCards/helpers/parsePaymentCards";

import { useAdjustTipApproval } from "./useAdjustTipApproval";
import { useApprovalDescription } from "./useApprovalDescription";
import { useRequestRefundApproval } from "./useRequestRefundApproval";

import { useTranslate } from "@/common/hooks/useTranslate";
import {
  ApprovalContent,
  AssistantConfig,
  DEFAULT_ASSISTANT_CONFIG,
} from "@/features/Assistant/contexts/AssistantConfigContext";
import { ToolPartDisplay } from "@/features/Assistant/Thread/ToolPart/helpers";

const getDonorToolPartDisplay = (part: ToolUIPart): ToolPartDisplay => {
  if (part.type === "tool-showPayments" && part.state === "output-available") {
    return parsePaymentCards(part.output).payments.length > 0 ? "content" : "hidden";
  }
  if (part.type === "tool-showOrganizationDashboards" && part.state === "output-available") {
    return "content";
  }
  if (
    (part.type === "tool-resendConfirmation" || part.type === "tool-adjustTip" || part.type === "tool-requestRefund") &&
    part.state === "output-available"
  ) {
    return "hidden";
  }
  return DEFAULT_ASSISTANT_CONFIG.getToolPartDisplay(part);
};

const useDonorApprovalContent = (part: ToolUIPart): ApprovalContent => {
  const { t } = useTranslate();
  const description = useApprovalDescription(part);
  const adjustTipApproval = useAdjustTipApproval(part);
  const requestRefundApproval = useRequestRefundApproval(part);
  if (adjustTipApproval) {
    return adjustTipApproval;
  }
  if (requestRefundApproval) {
    return requestRefundApproval;
  }
  return {
    description,
    noteLabel:
      part.type === "tool-requestHumanSupport"
        ? t("dashboard", "assistant.approval.requestHumanSupportNote")
        : undefined,
  };
};

export const DONOR_ASSISTANT_CONFIG: AssistantConfig = {
  feature: "donor-assistant",
  linkPolicy: DEFAULT_ASSISTANT_CONFIG.linkPolicy,
  getToolPartDisplay: getDonorToolPartDisplay,
  toolRenderers: {
    "tool-showPayments": ConnectedPaymentCards,
    "tool-showOrganizationDashboards": ConnectedOrganizationDashboards,
  },
  useApprovalContent: useDonorApprovalContent,
};
